import { IBroadcastTranslationShape } from "../../interfaces/esim/BroadcastNotifications.types";
import { appLanguages, BROADCAST_BODY_MAX, BROADCAST_TITLE_MAX } from "./broadcastStatus";

export interface IIncomingTranslation {
  languageCode?: string | null;
  title?: string | null;
  body?: string | null;
}

export interface IMergeArgs {
  current: IBroadcastTranslationShape[];
  incoming: IIncomingTranslation[];
  sourceLanguage: string;
  requested: string[];
  // Set when the source changed since the last run: existing translations now render text the
  // admin is no longer sending, so all of them are replaced rather than protected.
  overwriteExisting?: boolean;
}

export interface IMergeResult {
  rows: IBroadcastTranslationShape[];
  appliedCodes: string[]; // filled in place, replaced, or newly appended
  keptCodes: string[]; // had text and was protected (never set when overwriteExisting)
  missingCodes: string[]; // requested but nothing usable came back
  droppedCodes: string[]; // unknown code, echoed source, duplicate, or blank field
  tooLongCodes: string[]; // over the column limits; the backend shortens, so this is drift only
}

// Shared with useBroadcastTranslate so both sides trim identically.
export const clean = (raw?: string | null): string => (raw || "").trim();

// Canonicalises the API's casing to the exact appLanguages code so it matches an <option value>.
const canonicalCode = (raw?: string | null): string => {
  const code = clean(raw).toLowerCase();
  const match = appLanguages.filter((lang) => lang.code.toLowerCase() === code)[0];
  return match ? match.code : "";
};

const hasText = (row: IBroadcastTranslationShape): boolean =>
  !!clean(row.title) || !!clean(row.body);

// The languages to ask for: every app language except the source, minus any already written in —
// unless regenerateAll. Read from appLanguages at call time, so a new language needs no change here.
export const pendingTranslationTargets = (
  current: IBroadcastTranslationShape[],
  sourceLanguage: string,
  regenerateAll = false
): string[] => {
  const taken = regenerateAll
    ? []
    : (current || []).filter(hasText).map((row) => row.applicationLanguage);

  return appLanguages
    .map((lang) => lang.code)
    .filter((code) => code !== sourceLanguage && taken.indexOf(code) < 0);
};

// Folds generated translations into the form's existing rows: fill a row with a language but no
// text, append one for each remaining language, and protect rows the admin wrote — unless
// overwriteExisting (see IMergeArgs). Pure, so every edge case is exercisable without a form.
export const mergeBroadcastTranslations = (args: IMergeArgs): IMergeResult => {
  const { current, incoming, sourceLanguage, requested, overwriteExisting } = args;

  const usable: Record<string, IBroadcastTranslationShape> = {};
  const dropped: string[] = [];
  const tooLong: string[] = [];

  (incoming || []).forEach((item) => {
    const code = canonicalCode(item && item.languageCode);
    const title = clean(item && item.title);
    const body = clean(item && item.body);

    // Unknown code | echoed source | duplicate (first wins) | blank field. Blank matters: both
    // are schema-required, so a half-filled row would block submit.
    if (!code || code === sourceLanguage || usable[code] || !title || !body) {
      dropped.push(clean(item && item.languageCode) || "?");
      return;
    }

    if (title.length > BROADCAST_TITLE_MAX || body.length > BROADCAST_BODY_MAX) {
      tooLong.push(code);
    }

    usable[code] = { applicationLanguage: code, title, body };
  });

  const applied: string[] = [];
  const kept: string[] = [];

  // Pass 1 — walk the existing rows, preserving order and position.
  const rows = (current || []).map((row) => {
    const code = clean(row.applicationLanguage);
    const match = code ? usable[code] : undefined;

    if (!code || !match) return row;

    // Claimed either way: on the fill branch only, a filled language would still be in `usable`
    // at pass 2 and get a SECOND row appended — a duplicate the backend rejects outright.
    delete usable[code];

    if (hasText(row) && !overwriteExisting) {
      kept.push(code);
      return row;
    }

    applied.push(code);
    return match;
  });

  // Pass 2 — append the rest, in appLanguages order so new boxes match the dropdown.
  appLanguages.forEach((lang) => {
    const match = usable[lang.code];
    if (!match) return;

    delete usable[lang.code];
    applied.push(lang.code);
    rows.push(match);
  });

  return {
    rows,
    appliedCodes: applied,
    keptCodes: kept,
    missingCodes: (requested || []).filter(
      (code) => applied.indexOf(code) < 0 && kept.indexOf(code) < 0
    ),
    droppedCodes: dropped,
    tooLongCodes: tooLong
  };
};
